import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Pencil, Trash2, BookOpen, Settings, Wrench } from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";

interface KnowledgeEntry {
  id: string;
  title: string;
  content: string;
  category: 'system_context' | 'instructions' | 'tools_config';
  is_active: boolean;
  priority: number;
  created_at: string;
}

const categoryIcons = {
  system_context: BookOpen,
  instructions: Settings,
  tools_config: Wrench,
};

const categoryLabels = {
  system_context: 'Contexto do Sistema',
  instructions: 'Instruções',
  tools_config: 'Configuração de Ferramentas',
};

const AIKnowledge = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [formData, setFormData] = useState<{
    title: string;
    content: string;
    category: 'system_context' | 'instructions' | 'tools_config';
    is_active: boolean;
    priority: number;
  }>({
    title: '',
    content: '',
    category: 'system_context',
    is_active: true,
    priority: 5,
  });

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_knowledge_base')
        .select('*')
        .order('category')
        .order('priority', { ascending: false });

      if (error) throw error;
      setEntries((data || []) as KnowledgeEntry[]);
    } catch (error) {
      console.error('Error loading knowledge entries:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar base de conhecimento",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (selectedEntry) {
        const { error } = await supabase
          .from('ai_knowledge_base')
          .update(formData)
          .eq('id', selectedEntry.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('ai_knowledge_base')
          .insert([formData]);
        if (error) throw error;
      }

      toast({
        title: "Sucesso",
        description: selectedEntry ? "Entrada atualizada!" : "Entrada criada!",
      });

      setDialogOpen(false);
      resetForm();
      loadEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar entrada",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;

    try {
      const { error } = await supabase
        .from('ai_knowledge_base')
        .delete()
        .eq('id', selectedEntry.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Entrada deletada!",
      });

      setDeleteDialogOpen(false);
      setSelectedEntry(null);
      loadEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast({
        title: "Erro",
        description: "Erro ao deletar entrada",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (entry: KnowledgeEntry) => {
    try {
      const { error } = await supabase
        .from('ai_knowledge_base')
        .update({ is_active: !entry.is_active })
        .eq('id', entry.id);

      if (error) throw error;
      loadEntries();
    } catch (error) {
      console.error('Error toggling entry:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar entrada",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'system_context',
      is_active: true,
      priority: 5,
    });
    setSelectedEntry(null);
  };

  const openEditDialog = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry);
    setFormData({
      title: entry.title,
      content: entry.content,
      category: entry.category,
      is_active: entry.is_active,
      priority: entry.priority,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const groupedEntries = entries.reduce((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {} as Record<string, KnowledgeEntry[]>);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 overflow-auto p-6">
          <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Base de Conhecimento da IA</h1>
          <p className="text-muted-foreground">
            Configure o comportamento e contexto do assistente
          </p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Instrução
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(categoryLabels).map(([category, label]) => {
          const Icon = categoryIcons[category as keyof typeof categoryIcons];
          const categoryEntries = groupedEntries[category] || [];

          return (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon className="h-5 w-5" />
                  {label}
                </CardTitle>
                <CardDescription>
                  {categoryEntries.length} entrada(s)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {categoryEntries.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nenhuma entrada nesta categoria
                  </p>
                ) : (
                  categoryEntries.map((entry) => (
                    <Card key={entry.id} className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{entry.title}</h3>
                            <Badge variant={entry.is_active ? "default" : "secondary"}>
                              {entry.is_active ? 'Ativo' : 'Inativo'}
                            </Badge>
                            <Badge variant="outline">
                              Prioridade: {entry.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {entry.content}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={entry.is_active}
                            onCheckedChange={() => handleToggleActive(entry)}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(entry)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteDialog(entry)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEntry ? 'Editar Entrada' : 'Nova Entrada'}
            </DialogTitle>
            <DialogDescription>
              Configure uma instrução ou contexto para o assistente de IA
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ex: Análise de Duplicatas"
              />
            </div>

            <div>
              <Label htmlFor="category">Categoria</Label>
              <Select
                value={formData.category}
                onValueChange={(value: any) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="content">Conteúdo</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Descreva o contexto ou instrução..."
                rows={10}
              />
            </div>

            <div>
              <Label htmlFor="priority">
                Prioridade: {formData.priority}
              </Label>
              <Input
                id="priority"
                type="range"
                min="1"
                max="10"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar "{selectedEntry?.title}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AIKnowledge;
